import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const ParticleCanvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  opacity: 0.8;
  pointer-events: none;
`;

const ParticlesBackground = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let mouseX = 0;
    let mouseY = 0;
    let isMouseMoving = false;
    let mouseTimer;
    
    // Configuração do tamanho do canvas
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);
    
    // Rastreamento do mouse
    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      isMouseMoving = true;
      
      clearTimeout(mouseTimer);
      mouseTimer = setTimeout(() => {
        isMouseMoving = false;
      }, 2000);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    // Configuração das partículas
    const particlesArray = [];
    const numberOfParticles = Math.min(window.innerWidth / 8, 150); // Aumentando para 150 partículas
    
    // Cores em tons de azul e ciano com mais variação
    const colors = [
      'rgba(64, 128, 255, 0.8)',
      'rgba(32, 96, 200, 0.7)',
      'rgba(100, 182, 255, 0.9)',
      'rgba(52, 207, 235, 0.8)',
      'rgba(0, 255, 255, 0.7)',
      'rgba(0, 191, 255, 0.8)'
    ];
    
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 3 + 1;
        this.baseSize = this.size;
        this.speedX = Math.random() * 0.6 - 0.3;
        this.speedY = Math.random() * 0.6 - 0.3;
        this.baseSpeedX = this.speedX;
        this.baseSpeedY = this.speedY;
        this.color = colors[Math.floor(Math.random() * colors.length)];
        this.opacity = Math.random() * 0.5 + 0.3;
        this.pulseSpeed = Math.random() * 0.02 + 0.01;
        this.pulseAmount = Math.random() * 0.5 + 0.5;
        this.angle = Math.random() * Math.PI * 2;
        this.angleSpeed = Math.random() * 0.02 - 0.01;
        this.glowing = Math.random() > 0.7; // Algumas partículas terão efeito de brilho
        this.glowIntensity = 0;
        this.glowDirection = 1;
        this.glowSpeed = Math.random() * 0.03 + 0.01;
      }
      
      update() {
        // Efeito de pulso no tamanho
        this.size = this.baseSize + Math.sin(Date.now() * this.pulseSpeed) * this.pulseAmount;
        
        // Efeito de brilho para algumas partículas
        if (this.glowing) {
          this.glowIntensity += this.glowDirection * this.glowSpeed;
          if (this.glowIntensity > 1) {
            this.glowIntensity = 1;
            this.glowDirection = -1;
          } else if (this.glowIntensity < 0) {
            this.glowIntensity = 0;
            this.glowDirection = 1;
          }
        }
        
        // Interação com o mouse
        if (isMouseMoving) {
          const dx = mouseX - this.x;
          const dy = mouseY - this.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const maxDistance = 150;
          
          if (distance < maxDistance) {
            const force = (maxDistance - distance) / maxDistance;
            // Afastar do mouse
            this.speedX -= (dx / distance) * force * 0.2;
            this.speedY -= (dy / distance) * force * 0.2;
          } else {
            // Retornar gradualmente à velocidade base
            this.speedX = this.speedX * 0.95 + this.baseSpeedX * 0.05;
            this.speedY = this.speedY * 0.95 + this.baseSpeedY * 0.05;
          }
        } else {
          // Retornar gradualmente à velocidade base
          this.speedX = this.speedX * 0.98 + this.baseSpeedX * 0.02;
          this.speedY = this.speedY * 0.98 + this.baseSpeedY * 0.02;
        }
        
        // Movimento com um pouco de rotação
        this.angle += this.angleSpeed;
        this.x += this.speedX;
        this.y += this.speedY;
        
        // Voltar partículas que saem da tela para o lado oposto
        if (this.x > canvas.width) this.x = 0;
        else if (this.x < 0) this.x = canvas.width;
        
        if (this.y > canvas.height) this.y = 0;
        else if (this.y < 0) this.y = canvas.height;
      }
      
      draw() {
        ctx.fillStyle = this.color;
        ctx.globalAlpha = this.opacity;
        
        // Efeito de brilho para algumas partículas
        if (this.glowing && this.glowIntensity > 0) {
          const glow = ctx.createRadialGradient(
            this.x, this.y, this.size * 0.5,
            this.x, this.y, this.size * (3 + this.glowIntensity * 2)
          );
          glow.addColorStop(0, this.color);
          glow.addColorStop(1, 'rgba(64, 128, 255, 0)');
          
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size * (3 + this.glowIntensity * 2), 0, Math.PI * 2);
          ctx.fill();
        }
        
        // Desenhar a partícula
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    const init = () => {
      for (let i = 0; i < numberOfParticles; i++) {
        particlesArray.push(new Particle());
      }
    };
    
    init();
    
    // Conexões entre partículas (efeito de rede)
    const connect = () => {
      const maxDistance = 180;
      
      for (let a = 0; a < particlesArray.length; a++) {
        for (let b = a; b < particlesArray.length; b++) {
          const dx = particlesArray[a].x - particlesArray[b].x;
          const dy = particlesArray[a].y - particlesArray[b].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < maxDistance) {
            const opacity = 1 - (distance / maxDistance);
            
            // Gradiente para as linhas de conexão
            const gradient = ctx.createLinearGradient(
              particlesArray[a].x, particlesArray[a].y,
              particlesArray[b].x, particlesArray[b].y
            );
            
            // Usar cores das partículas para o gradiente
            gradient.addColorStop(0, particlesArray[a].color.replace(/[^,]+(?=\))/, `${opacity * 0.5}`));
            gradient.addColorStop(1, particlesArray[b].color.replace(/[^,]+(?=\))/, `${opacity * 0.5}`));
            
            ctx.strokeStyle = gradient;
            ctx.lineWidth = opacity * 0.8;
            ctx.beginPath();
            ctx.moveTo(particlesArray[a].x, particlesArray[a].y);
            ctx.lineTo(particlesArray[b].x, particlesArray[b].y);
            ctx.stroke();
          }
        }
      }
      
      // Conexões adicionais com o cursor do mouse quando estiver se movendo
      if (isMouseMoving) {
        const mouseMaxDistance = 200;
        for (let i = 0; i < particlesArray.length; i++) {
          const dx = mouseX - particlesArray[i].x;
          const dy = mouseY - particlesArray[i].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < mouseMaxDistance) {
            const opacity = 1 - (distance / mouseMaxDistance);
            const gradient = ctx.createLinearGradient(
              mouseX, mouseY,
              particlesArray[i].x, particlesArray[i].y
            );
            
            gradient.addColorStop(0, `rgba(255, 255, 255, ${opacity * 0.7})`);
            gradient.addColorStop(1, particlesArray[i].color.replace(/[^,]+(?=\))/, `${opacity * 0.5}`));
            
            ctx.strokeStyle = gradient;
            ctx.lineWidth = opacity * 1.2;
            ctx.beginPath();
            ctx.moveTo(mouseX, mouseY);
            ctx.lineTo(particlesArray[i].x, particlesArray[i].y);
            ctx.stroke();
          }
        }
      }
    };
    
    // Efeito de fundo digital
    const drawDigitalBackground = () => {
      ctx.globalAlpha = 0.05;
      ctx.fillStyle = '#0a192f';
      
      // Grade digital
      const gridSize = 30;
      const time = Date.now() * 0.001;
      
      for (let x = 0; x < canvas.width; x += gridSize) {
        for (let y = 0; y < canvas.height; y += gridSize) {
          const distanceToCenter = Math.sqrt(
            Math.pow(x - canvas.width / 2, 2) + 
            Math.pow(y - canvas.height / 2, 2)
          );
          
          const pulseSize = Math.sin(time + distanceToCenter * 0.01) * 0.5 + 0.5;
          const size = gridSize * 0.1 + gridSize * 0.05 * pulseSize;
          
          if (Math.random() > 0.996) { // Ocasionalmente destaca um ponto
            ctx.fillStyle = 'rgba(64, 128, 255, 0.3)';
            ctx.fillRect(x - size/2, y - size/2, size * 2, size * 2);
            ctx.fillStyle = '#0a192f';
          } else {
            ctx.fillRect(x - size/2, y - size/2, size, size);
          }
        }
      }
      
      ctx.globalAlpha = 1;
    };
    
    // Loop de animação
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Desenhar efeito de fundo
      drawDigitalBackground();
      
      // Atualizar e desenhar partículas
      for (let i = 0; i < particlesArray.length; i++) {
        particlesArray[i].update();
        particlesArray[i].draw();
      }
      
      // Conectar partículas
      connect();
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animate();
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', setCanvasSize);
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(mouseTimer);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  
  return <ParticleCanvas ref={canvasRef} />;
};

export default ParticlesBackground;
